import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  Button,
  Card,
  Empty,
  Grid,
  Input,
  List,
  Modal,
  Pagination,
  Select,
  Space,
  Table,
  Tag,
  Tabs,
  Tooltip,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PATHS } from "../../../app/router/paths";
import { normalizeApiError } from "../../../shared/errors/normalizeApiError";
import { appNotification } from "../../../shared/notifications/appNotification";
import { PageHeader } from "../../../shared/components/PageHeader";
import { MobilePagination } from "../../../shared/components/MobilePagination";
import {
  deletePropose,
  fetchProposes,
  type ProposeListSort,
  type ProposeSalesBucket,
} from "../api/proposesApi";
import type { PaginationMeta } from "../../../shared/api/pagination";
import type { Propose } from "../model/types";
import { formatRepresentativeId } from "../model/proposeEditor";
import { statusColor, statusLabel } from "../model/proposeStatus";
import { useAuth } from "../../auth/model/AuthContext";
import { usePageRefresh } from "../../../shared/pull-refresh/PullRefreshContext";
import "../styles/ProposeList.css";

const PAGE_SIZE = 10;
const { useBreakpoint } = Grid;
type SaleProposalRow = {
  proposal: Propose;
  urgency?: string;
  hasActiveSubmission?: boolean;
};
const displayDate = (value?: string | null) =>
  value ? dayjs(value).format("YYYY/MM/DD") : "—";
const displayDateTime = (value?: string | null) =>
  value ? dayjs(value).format("YYYY/MM/DD HH:mm") : "—";
const deadlineRemaining = (value: string | null | undefined, now: number) => {
  if (!value) return "—";
  const remainingMilliseconds = dayjs(value).diff(now);
  if (remainingMilliseconds <= 0) return "期限切れ";
  if (remainingMilliseconds < 60_000) return "1分未満";
  const remainingMinutes = Math.floor(remainingMilliseconds / 60_000);
  const days = Math.floor(remainingMinutes / (24 * 60));
  const hours = Math.floor((remainingMinutes % (24 * 60)) / 60);
  const minutes = remainingMinutes % 60;
  if (days) return `${days}日 ${hours}時間`;
  if (hours) return `${hours}時間 ${minutes}分`;
  return `${minutes}分`;
};
const hasActiveInputWindow = (proposal: Propose, now: number) =>
  proposal.status === "INPUTTING" &&
  Boolean(proposal.expires_at) &&
  dayjs(proposal.expires_at).isAfter(now);
const deadlineUrgency = (proposal: Propose, now: number) => {
  if (!hasActiveInputWindow(proposal, now)) return undefined;
  const remaining = dayjs(proposal.expires_at).diff(now);
  if (remaining < 60 * 60 * 1000) return "propose-deadline-critical";
  if (remaining < 3 * 60 * 60 * 1000) return "propose-deadline-warning";
  return undefined;
};
const statusOptions = [
  { value: "DRAFT", label: "下書き" },
  { value: "INPUTTING", label: "入力中" },
  { value: "REVIEW", label: "確認待ち" },
  { value: "APPROVED", label: "承認済み" },
  { value: "REJECTED", label: "却下" },
  { value: "SENT", label: "送信済み" },
];

export const ProposeListPage = () => {
  const registerPageRefresh = usePageRefresh();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const navigate = useNavigate();
  const { user } = useAuth();
  const isCoordinator = Boolean(
    user?.permissions.includes("propose.coordinator.manage"),
  );
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [proposes, setProposes] = useState<Propose[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>();
  const [coordinatorStatus, setCoordinatorStatus] =
    useState<Propose["status"]>();
  const [salesHistoryStatus, setSalesHistoryStatus] =
    useState<Propose["status"]>();
  const [sort, setSort] = useState<ProposeListSort>();
  const [salesBucket, setSalesBucket] = useState<ProposeSalesBucket>("active");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  const appliedSalesBucket = isCoordinator ? undefined : salesBucket;
  const appliedStatus = isCoordinator
    ? coordinatorStatus
    : salesBucket === "history"
      ? salesHistoryStatus
      : undefined;
  const saleProposals = useMemo(
    () =>
      proposes.map((proposal) => ({
        proposal,
        urgency:
          appliedSalesBucket === "active"
            ? deadlineUrgency(proposal, now)
            : undefined,
        hasActiveSubmission:
          proposal.has_sale_submission && hasActiveInputWindow(proposal, now),
      })),
    [appliedSalesBucket, now, proposes],
  );

  const loadProposals = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchProposes({
        page,
        pageSize,
        status: appliedStatus,
        search,
        sort,
        salesBucket: appliedSalesBucket,
      });
      setProposes(result.items);
      setPagination(result.pagination);
    } catch (error) {
      appNotification.error({ message: normalizeApiError(error).message });
    } finally {
      setLoading(false);
    }
  }, [appliedSalesBucket, appliedStatus, page, pageSize, search, sort]);

  useEffect(() => {
    void loadProposals();
  }, [loadProposals]);
  useEffect(() => {
    registerPageRefresh({ enabled: true, refresh: loadProposals });
    return () => registerPageRefresh(null);
  }, [registerPageRefresh, loadProposals]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setSearch(searchInput.trim());
    }, 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const goToDetail = (proposal: Propose) =>
    navigate(`${PATHS.PROPOSES.VIEW}?id=${proposal.propose_id}`);
  const goToEdit = (proposal: Propose) =>
    navigate(`${PATHS.PROPOSES.EDIT}?id=${proposal.propose_id}`);
  const goToSalesEdit = (proposal: Propose) =>
    navigate(`${PATHS.PROPOSES.VIEW}?id=${proposal.propose_id}&mode=edit`);
  const remove = async (proposal: Propose) => {
    try {
      await deletePropose(proposal.propose_id);
      appNotification.success({ message: "提案書を削除しました" });
      if (proposes.length === 1 && page > 1) setPage(page - 1);
      else void loadProposals();
    } catch (error) {
      appNotification.error({ message: normalizeApiError(error).message });
    }
  };
  const confirmRemove = (proposal: Propose) => {
    Modal.confirm({
      title: "この提案書を削除しますか？",
      content: "削除後は復元できず、一覧にも表示されません。",
      okText: "削除",
      cancelText: "キャンセル",
      okButtonProps: { danger: true },
      onOk: () => remove(proposal),
    });
  };
  const actionButtons = (proposal: Propose) => (
    <Space
      className="propose-list-actions"
      size={4}
      onClick={(event) => event.stopPropagation()}
    >
      <Tooltip title="詳細を表示">
        <Button
          shape="circle"
          icon={<EyeOutlined />}
          aria-label="詳細を表示"
          onClick={() => goToDetail(proposal)}
        />
      </Tooltip>
      <Tooltip
        title={
          proposal.status === "DRAFT"
            ? "編集"
            : "下書きの提案書のみ編集できます"
        }
      >
        <Button
          type="primary"
          shape="circle"
          icon={<EditOutlined />}
          aria-label="編集"
          disabled={proposal.status !== "DRAFT"}
          onClick={() => goToEdit(proposal)}
        />
      </Tooltip>
      <Tooltip
        title={
          proposal.status === "DRAFT"
            ? "削除"
            : "下書きの提案書のみ削除できます"
        }
      >
        <span>
          <Button
            danger
            shape="circle"
            icon={<DeleteOutlined />}
            aria-label="削除"
            disabled={proposal.status !== "DRAFT"}
            onClick={() => confirmRemove(proposal)}
          />
        </span>
      </Tooltip>
    </Space>
  );
  const mobileActionButtons = (proposal: Propose) => (
    <div
      className="propose-mobile-card-actions"
      onClick={(event) => event.stopPropagation()}
    >
      <Button
        type="default"
        icon={<EyeOutlined />}
        onClick={() => goToDetail(proposal)}
      >
        詳細
      </Button>
      <Button
        type="primary"
        icon={<EditOutlined />}
        disabled={proposal.status !== "DRAFT"}
        onClick={() => goToEdit(proposal)}
      >
        編集
      </Button>
      <Button
        danger
        icon={<DeleteOutlined />}
        disabled={proposal.status !== "DRAFT"}
        onClick={() => confirmRemove(proposal)}
      >
        削除
      </Button>
    </div>
  );
  const saleProposalCard = ({
    proposal,
    urgency,
    hasActiveSubmission,
  }: SaleProposalRow) => (
    <article
      className={`propose-mobile-card propose-mobile-proposal-card ${urgency || ""} ${hasActiveSubmission ? "propose-has-sale-submission" : ""}`}
      onClick={() => goToDetail(proposal)}
    >
      <div className="propose-mobile-card-header">
        <strong className="propose-link">{proposal.propose_code}</strong>
        <Tag color={statusColor(proposal.status)}>
          {statusLabel(proposal.status)}
        </Tag>
      </div>
      <div className="propose-sale-mobile-company">
        <span>会社名</span>
        <strong>{proposal.company_name || "—"}</strong>
        <small>
          買受人コード：{formatRepresentativeId(proposal.representative_id)}
        </small>
      </div>
      <div className="propose-sale-mobile-details">
        <div>
          <span>買受人担当者名</span>
          <strong>{proposal.recipient_name || "—"}</strong>
        </div>
        <div>
          <span>提案期間</span>
          <strong>
            {displayDate(proposal.effective_from)} ～{" "}
            {displayDate(proposal.effective_to)}
          </strong>
        </div>
      </div>
      <div className="propose-sale-mobile-deadline">
        <div>
          <span>営業入力期限</span>
          <strong>{displayDateTime(proposal.expires_at)}</strong>
        </div>
        <div>
          <span>期限まで</span>
          <strong>{deadlineRemaining(proposal.expires_at, now)}</strong>
        </div>
      </div>
      <div
        className={`propose-mobile-card-actions ${proposal.can_sale_edit && hasActiveInputWindow(proposal, now) ? "propose-sale-card-actions--editable" : ""}`}
        onClick={(event) => event.stopPropagation()}
      >
        <Button icon={<EyeOutlined />} onClick={() => goToDetail(proposal)}>
          表示
        </Button>
        {proposal.can_sale_edit && hasActiveInputWindow(proposal, now) && (
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => goToSalesEdit(proposal)}
          >
            入力を編集
          </Button>
        )}
      </div>
    </article>
  );

  if (!isCoordinator)
    return (
      <main className="propose-page">
        <PageHeader
          title="販売提案"
          description="担当商品の入力依頼"
          actionLabel=""
          onAction={() => undefined}
          isbutton={false}
        />
        <Card className="deadline-list-card propose-list-card" bordered={false}>
          <div className="propose-sale-list-filters">
            <Input
              size="large"
              prefix={<SearchOutlined />}
              allowClear
              placeholder="提案番号・買受人コード・会社名・買受人担当者名で検索"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
            {salesBucket === "history" && (
              <Select
                size="large"
                value={salesHistoryStatus}
                allowClear
                placeholder="すべての状態"
                options={statusOptions.filter(
                  (option) => option.value !== "DRAFT",
                )}
                onChange={(value) => {
                  setPage(1);
                  setSalesHistoryStatus(value);
                }}
              />
            )}
            <Select
              size="large"
              value={sort}
              allowClear
              placeholder="入力期限で並び替え"
              options={[
                { value: "expires_at_asc", label: "入力期限が近い順" },
                { value: "expires_at_desc", label: "入力期限が遠い順" },
              ]}
              onChange={(value) => {
                setPage(1);
                setSort(value);
              }}
            />
          </div>
          <Tabs
            className="propose-sale-deadline-tabs"
            activeKey={salesBucket}
            onChange={(value) => {
              setPage(1);
              setSalesBucket(value as ProposeSalesBucket);
            }}
            items={[
              { key: "active", label: "新しい提案" },
              { key: "history", label: "過去の提案" },
            ]}
          />
          {isMobile ? (
            <div className="propose-mobile-list">
              <List
                loading={loading}
                dataSource={saleProposals}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="提案書がありません"
                    />
                  ),
                }}
                renderItem={(saleProposal) => (
                  <List.Item className="propose-mobile-list-item">
                    {saleProposalCard(saleProposal)}
                  </List.Item>
                )}
              />
              <MobilePagination
                current={page}
                pageSize={pageSize}
                total={pagination?.total || 0}
                onChange={setPage}
              />
            </div>
          ) : (
            <div className="propose-sale-desktop-list">
              <List
                className="propose-sale-card-list"
                loading={loading}
                dataSource={saleProposals}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description="提案書がありません"
                    />
                  ),
                }}
                renderItem={(saleProposal) => (
                  <List.Item className="propose-sale-card-list-item">
                    {saleProposalCard(saleProposal)}
                  </List.Item>
                )}
              />
              <Pagination
                current={page}
                pageSize={pageSize}
                total={pagination?.total || 0}
                size="middle"
                showSizeChanger
                pageSizeOptions={[10, 20, 50, 100]}
                showTotal={(total, range) =>
                  `${total} 件中 ${range[0]}-${range[1]} を表示`
                }
                onChange={(nextPage, nextPageSize) => {
                  setPage(nextPageSize !== pageSize ? 1 : nextPage);
                  setPageSize(nextPageSize);
                }}
                style={{marginTop: 60}}
              />
            </div>
          )}
        </Card>
      </main>
    );

  return (
    <main className="propose-page">
      <PageHeader
        title="販売提案管理"
        description="販売提案書を作成・管理します"
        actionLabel="新規提案を作成"
        onAction={() => navigate(PATHS.PROPOSES.CREATE)}
      />
      <Card className="deadline-list-card propose-list-card" bordered={false}>
        <div
          className={
            isMobile ? "propose-list-toolbar-mb" : "propose-list-toolbar-pc"
          }
        >
          <Input
            size="large"
            prefix={<SearchOutlined />}
            allowClear
            placeholder="提案番号・買受人コード・会社名・買受人担当者名で検索"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <Select
            value={coordinatorStatus}
            size="large"
            allowClear
            placeholder="すべての状態"
            options={statusOptions}
            onChange={(value) => {
              setPage(1);
              setCoordinatorStatus(value);
            }}
          />
          <Select
            value={sort}
            size="large"
            allowClear
            placeholder="入力期限で並び替え"
            options={[
              { value: "expires_at_asc", label: "入力期限が近い順" },
              { value: "expires_at_desc", label: "入力期限が遠い順" },
            ]}
            onChange={(value) => {
              setPage(1);
              setSort(value);
            }}
          />
        </div>
        {isMobile ? (
          <div className="propose-mobile-list">
            <List
              loading={loading}
              dataSource={proposes}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="提案書がありません"
                  />
                ),
              }}
              renderItem={(proposal) => (
                <List.Item className="propose-mobile-list-item">
                  <article
                    className="propose-mobile-card propose-mobile-proposal-card"
                    onClick={() => goToDetail(proposal)}
                  >
                    <div className="propose-mobile-card-header">
                      <strong className="propose-link">
                        {proposal.propose_code}
                      </strong>
                      <Tag color={statusColor(proposal.status)}>
                        {statusLabel(proposal.status)}
                      </Tag>
                    </div>
                    <div className="propose-mobile-meta">
                      <span>買受人コード</span>
                      <strong>
                        {formatRepresentativeId(proposal.representative_id)}
                      </strong>
                      <span>買受人担当者名</span>
                      <strong>{proposal.recipient_name || "—"}</strong>
                      <span>有効期間</span>
                      <strong>
                        {displayDate(proposal.effective_from)} ～{" "}
                        {displayDate(proposal.effective_to)}
                      </strong>
                      <span>営業入力期限</span>
                      <strong>{displayDateTime(proposal.expires_at)}</strong>
                    </div>
                    {mobileActionButtons(proposal)}
                  </article>
                </List.Item>
              )}
            />
            <MobilePagination
              current={page}
              pageSize={pageSize}
              total={pagination?.total || 0}
              onChange={setPage}
            />
          </div>
        ) : (
          <Table<Propose>
            className="propose-table"
            rowKey="propose_id"
            loading={loading}
            dataSource={proposes}
            tableLayout="fixed"
            scroll={{ x: 1120 }}
            onRow={(proposal) => ({
              onClick: () => goToDetail(proposal),
            })}
            pagination={{
              current: page,
              pageSize,
              total: pagination?.total || 0,
              size: "middle",
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              showTotal: (total, range) =>
                `${total} 件中 ${range[0]}-${range[1]} を表示`,
              onChange: (nextPage, nextPageSize) => {
                setPage(nextPageSize !== pageSize ? 1 : nextPage);
                setPageSize(nextPageSize);
              },
            }}
            columns={[
              {
                title: "提案書コード",
                dataIndex: "propose_code",
                width: 160,
                render: (value) => (
                  <span className="propose-link">{value}</span>
                ),
              },
              {
                title: "買受人コード",
                dataIndex: "representative_id",
                width: 150,
                render: formatRepresentativeId,
              },
              {
                title: "買受人担当者名",
                dataIndex: "recipient_name",
                width: 150,
                render: (value) => value || "—",
              },
              {
                title: "有効期間",
                width: 220,
                render: (_, row) =>
                  `${displayDate(row.effective_from)} ～ ${displayDate(row.effective_to)}`,
              },
              {
                title: "状態",
                dataIndex: "status",
                width: 120,
                render: (value) => (
                  <Tag color={statusColor(value)}>{statusLabel(value)}</Tag>
                ),
              },
              {
                title: "営業入力期限",
                dataIndex: "expires_at",
                width: 160,
                render: displayDateTime,
              },
              {
                title: "操作",
                key: "actions",
                width: 148,
                fixed: screens.lg ? "right" : undefined,
                align: "center",
                render: (_, proposal) => actionButtons(proposal),
              },
            ]}
          />
        )}
      </Card>
    </main>
  );
};
